<template>
  <div class="video-container">
    <!-- Placeholder image shown until video loads -->
    <img 
      v-if="!videoReady"
      :src="resolvedPlaceholderImage" 
      :alt="altText" 
      class="video-placeholder"
    />
    <!-- Video element with responsive sources -->
    <video
      autoplay
      muted
      loop
      playsinline
      :id="videoId"
      class="bg-video"
      :class="{'video-loaded': videoReady}"
      @canplay="handleVideoLoad"
      @error="handleVideoError"
    >
      <source :src="resolvedDesktopVideo" type="video/mp4" media="(min-width: 769px)" />
      <source :src="resolvedMobileVideo" type="video/mp4" media="(max-width: 768px)" />
      Your browser does not support the video tag.
    </video>
    <!-- Loading spinner -->
    <div v-if="videoLoading && !imageLoaded" class="loading-spinner">
      <font-awesome-icon :icon="['fas', 'spinner']" spin />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BackgroundVideo',
  props: {
    desktopVideo: {
      type: String,
      required: true
    },
    mobileVideo: {
      type: String,
      default: function() {
        return this.desktopVideo; // Fallback to desktop video if mobile not provided
      }
    },
    placeholderImage: {
      type: String,
      required: true
    },
    altText: {
      type: String,
      default: 'Video placeholder'
    },
    videoId: {
      type: String,
      default: 'bgVideo'
    }
  },
  data() {
    return {
      videoLoading: true,
      videoReady: false,
      imageLoaded: true // Default true to avoid showing spinner if image loads quickly
    };
  },
  computed: {
    // Resolve paths to avoid @ alias issues in template
    resolvedDesktopVideo() {
      return this.resolvePath(this.desktopVideo);
    },
    resolvedMobileVideo() {
      return this.resolvePath(this.mobileVideo);
    },
    resolvedPlaceholderImage() {
      return this.resolvePath(this.placeholderImage);
    }
  },
  methods: {
    // Handle @ alias properly
    resolvePath(path) {
      if (path.startsWith('@/')) {
        // Remove the @ and replace with the correct public path
        return path.replace('@/', '/'); 
      }
      return path;
    },
    handleVideoLoad() {
      this.videoLoading = false;
      this.videoReady = true;
      
      // Ensure video starts playing
      const video = document.getElementById(this.videoId);
      if (video) {
        video.play().catch(err => console.error('Error playing video:', err));
      }
      
      this.$emit('video-loaded');
    },
    handleVideoError() {
      console.error('Error loading video');
      this.videoLoading = false;
      this.$emit('video-error');
    }
  }
};
</script>

<style scoped>
.video-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.bg-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.bg-video.video-loaded {
  z-index: 2;
  opacity: 1;
}

.video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  color: white;
}
</style>
